import React, { useState, useEffect } from "react";
import { Modal, Input, Button, List, Card, Checkbox, Row, Col, Select, Switch, Space, Typography } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface AgentsModalProps {
  open: boolean;
  onClose: () => void;
  formData: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSave: () => void;
  handleUpdate: () => void;
  action: { type: string } | null;
  loading: boolean;
}

interface Parameter {
  paramName: string;
  type: string;
  description: string;
  required: boolean;
}

interface ActionGroup {
  name: string;
  description: string;
  lambdaArn: string;
  parameters: Parameter[];
}

const AgentsModal: React.FC<AgentsModalProps> = ({
  open,
  onClose,
  formData,
  handleChange,
  handleSave,
  handleUpdate,
  action,
  loading,
}) => {
  const isEdit = action?.type === "edit";

  const [actionGroupInput, setActionGroupInput] = useState<ActionGroup>({
    name: "",
    description: "",
    lambdaArn: "",
    parameters: [],
  });
  const [actionGroupsList, setActionGroupsList] = useState<ActionGroup[]>([]);
  const [newParameter, setNewParameter] = useState<Parameter>({
    paramName: "",
    type: "string",
    description: "",
    required: false,
  });
  const [associateSupervisor, setAssociateSupervisor] = useState(false);
  const [supervisorId, setSupervisorId] = useState("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open) {
      try {
        const parsed = JSON.parse(formData.actionGroups || "[]");
        setActionGroupsList(parsed);
      } catch (e) {
        setActionGroupsList([]);
      }
      setAssociateSupervisor(formData.associateSupervisor || false);
      setSupervisorId(formData.supervisorId || "");
      setErrors({});
    }
  }, [open, formData.actionGroups, formData.associateSupervisor, formData.supervisorId]);

  const validateFields = (): { [key: string]: string } => {
    const errs: { [key: string]: string } = {};
    if (!formData.agentName || !formData.agentName.trim()) {
      errs.agentName = "El nombre del Agente es obligatorio.";
    }
    if (!formData.usage || !formData.usage.trim()) {
      errs.usage = "El uso del Agente es obligatorio.";
    }
    if (!formData.instructions || !formData.instructions.trim()) {
      errs.instructions = "Las instrucciones son obligatorias.";
    }
    if (!formData.agentCategory || !formData.agentCategory.trim()) {
      errs.agentCategory = "La categoría del Agente es obligatoria.";
    }
    if (!formData.establishment_id_knowledge_base || !formData.establishment_id_knowledge_base.trim()) {
      errs.establishment_id_knowledge_base = "La base de conocimientos es obligatoria.";
    }
    if (formData.requiresParameters === "true" || formData.requiresParameters === true) {
      let actionGroups;
      try {
        actionGroups = JSON.parse(formData.actionGroups);
      } catch (e) {
        errs.actionGroups = "Los grupos de acción tienen un formato incorrecto.";
      }
      if (!actionGroups || actionGroups.length === 0) {
        errs.actionGroups = "Debe agregar al menos un Action Group.";
      }
    }
    if (associateSupervisor && (!supervisorId || !supervisorId.trim())) {
      errs.supervisorId = "El ID del Supervisor es obligatorio.";
    }
    return errs;
  };

  const onSubmit = () => {
    const errs = validateFields();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    if (isEdit) {
      handleUpdate();
    } else {
      handleSave();
    }
  };

  const handleAddParameter = () => {
    // Si ya se alcanzó el límite, no se agrega
    if (actionGroupInput.parameters.length >= 5) {
      return;
    }
    if (!newParameter.paramName.trim()) {
      setErrors((prev) => ({
        ...prev,
        newParameter: "El nombre del parámetro es obligatorio.",
      }));
      return;
    }
    setActionGroupInput((prev) => ({
      ...prev,
      parameters: [...prev.parameters, newParameter],
    }));
    setNewParameter({ paramName: "", type: "string", description: "", required: false });
    setErrors((prev) => ({ ...prev, newParameter: "" }));
  };

  const handleDeleteParameter = (index: number) => {
    setActionGroupInput((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index),
    }));
  };

  const handleAddActionGroup = () => {
    if (!actionGroupInput.name.trim()) {
      setErrors((prev) => ({
        ...prev,
        actionGroupInput: "El nombre del Action Group es obligatorio.",
      }));
      return;
    }
    if (!actionGroupInput.lambdaArn.trim()) {
      setErrors((prev) => ({
        ...prev,
        actionGroupInputLambda: "El Lambda ARN es obligatorio.",
      }));
      return;
    }
    const updatedList = [...actionGroupsList, { ...actionGroupInput }];
    setActionGroupsList(updatedList);
    
    // Update parent state directly
    handleChange({
      target: { id: "actionGroups", value: JSON.stringify(updatedList) },
    } as React.ChangeEvent<HTMLInputElement>);
    
    setActionGroupInput({ name: "", description: "", lambdaArn: "", parameters: [] });
    setErrors((prev) => ({
      ...prev,
      actionGroupInput: "",
      actionGroupInputLambda: "",
    }));
  };

  const handleDeleteActionGroup = (index: number) => {
    const updatedList = actionGroupsList.filter((_, i) => i !== index);
    setActionGroupsList(updatedList);
    
    // Update parent state directly
    handleChange({
      target: { id: "actionGroups", value: JSON.stringify(updatedList) },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleAssociateSupervisorChange = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setAssociateSupervisor(checked);
    
    // Update parent state directly
    handleChange({
      target: { id: "associateSupervisor", value: String(checked) },
    } as React.ChangeEvent<HTMLInputElement>);
    
    if (!checked) {
      setSupervisorId("");
      handleChange({
        target: { id: "supervisorId", value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  
  // Update the setSupervisorId handler to also update parent state
  const handleSupervisorIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSupervisorId(value);
    handleChange({
      target: { id: "supervisorId", value },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  // Variable para controlar el máximo de parámetros
  const maxParamsReached = actionGroupInput.parameters.length >= 5;

  return (
    <Modal
      title={isEdit ? "Editar Agente" : "Crear Agente"}
      open={open}
      onCancel={!loading ? onClose : undefined}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" onClick={onSubmit} loading={loading} disabled={loading}>
          {isEdit ? "Actualizar" : "Crear"}
        </Button>,
      ]}
      width={1200}
      maskClosable={!loading}
      closable={!loading}
    >
      <Row gutter={24}>
        <Col span={12}>
          <Card title="Detalles del Agente" bordered={false}>
            <Row gutter={16}>
              <Col span={24}>
                <Input
                  id="agentName"
                  placeholder="Nombre del Agente"
                  value={formData.agentName}
                  onChange={handleChange}
                  style={{ marginBottom: "5px" }}
                  disabled={loading}
                  status={errors.agentName ? "error" : ""}
                  suffix={errors.agentName ? <ClockCircleOutlined /> : null}
                />
                {errors.agentName && <span style={{ color: "red", fontSize: "12px" }}>{errors.agentName}</span>}
              </Col>
              <Col span={24}>
                <Input
                  id="usage"
                  placeholder="Uso del Agente"
                  value={formData.usage}
                  onChange={handleChange}
                  style={{ marginBottom: "5px" }}
                  disabled={loading}
                  status={errors.usage ? "error" : ""}
                  suffix={errors.usage ? <ClockCircleOutlined /> : null}
                />
                {errors.usage && <span style={{ color: "red", fontSize: "12px" }}>{errors.usage}</span>}
              </Col>
              <Col span={24}>
                <TextArea
                  id="instructions"
                  placeholder="Instrucciones del Agente"
                  value={formData.instructions}
                  onChange={handleChange}
                  style={{ marginBottom: "5px" }}
                  disabled={loading}
                  rows={4}
                  status={errors.instructions ? "error" : ""}
                />
                {errors.instructions && (
                  <span style={{ color: "red", fontSize: "12px" }}>{errors.instructions}</span>
                )}
              </Col>
              <Col span={24}>
                <Input
                  id="agentCategory"
                  placeholder="Categoría del Agente"
                  value={formData.agentCategory}
                  onChange={handleChange}
                  style={{ marginBottom: "5px" }}
                  disabled={loading}
                  status={errors.agentCategory ? "error" : ""}
                  suffix={errors.agentCategory ? <ClockCircleOutlined /> : null}
                />
                {errors.agentCategory && (
                  <span style={{ color: "red", fontSize: "12px" }}>{errors.agentCategory}</span>
                )}
              </Col>
              <Col span={24}>
                <Input
                  id="establishment_id_knowledge_base"
                  placeholder="Base de Conocimientos del Agente"
                  value={formData.establishment_id_knowledge_base}
                  onChange={handleChange}
                  style={{ marginBottom: "5px" }}
                  disabled={loading}
                  status={errors.establishment_id_knowledge_base ? "error" : ""}
                  suffix={errors.establishment_id_knowledge_base ? <ClockCircleOutlined /> : null}
                />
                {errors.establishment_id_knowledge_base && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.establishment_id_knowledge_base}
                  </span>
                )}
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Configuración Avanzada" bordered={false}>
            <Row gutter={16}>
              <Col span={12}>
                <Space style={{ marginBottom: "15px" }}>
                  <span>Agente General:</span>
                  <Switch
                    checked={formData.isGeneral === "true" || formData.isGeneral === true}
                    onChange={(checked: boolean) =>
                      handleChange({
                        target: { id: "isGeneral", value: String(checked) },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    disabled={loading}
                  />
                </Space>
              </Col>
              <Col span={12}>
                <Space style={{ marginBottom: "15px" }}>
                  <span>Requiere Parámetros:</span>
                  <Switch
                    checked={formData.requiresParameters === "true" || formData.requiresParameters === true}
                    onChange={(checked: boolean) =>
                      handleChange({
                        target: { id: "requiresParameters", value: String(checked) },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    disabled={loading}
                  />
                </Space>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <div
                  style={{
                    marginBottom: "15px",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "5px",
                  }}
                >
                  <Checkbox
                    checked={associateSupervisor}
                    onChange={handleAssociateSupervisorChange}
                    disabled={loading}
                  >
                    Asociar a Supervisor
                  </Checkbox>
                  {associateSupervisor && (
                    <>
                      <Input
                        id="supervisorId"
                        placeholder="Ingrese el ID del Supervisor"
                        value={supervisorId}
                        onChange={handleSupervisorIdChange}
                        style={{ marginTop: "10px" }}
                        disabled={loading}
                        status={errors.supervisorId ? "error" : ""}
                        suffix={errors.supervisorId ? <ClockCircleOutlined /> : null}
                      />
                      {errors.supervisorId && (
                        <span style={{ color: "red", fontSize: "12px" }}>{errors.supervisorId}</span>
                      )}
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {(formData.requiresParameters === "true" || formData.requiresParameters === true) && (
        <Card title="Action Groups" style={{ marginTop: "20px" }} bordered>
          <Row gutter={16}>
            <Col span={8}>
              <Input
                placeholder="Nombre del Action Group"
                value={actionGroupInput.name}
                onChange={(e) =>
                  setActionGroupInput((prev) => ({ ...prev, name: e.target.value }))
                }
                style={{ marginBottom: "5px" }}
                disabled={loading}
                status={errors.actionGroupInput ? "error" : ""}
                suffix={errors.actionGroupInput ? <ClockCircleOutlined /> : null}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Descripción del Action Group"
                value={actionGroupInput.description}
                onChange={(e) =>
                  setActionGroupInput((prev) => ({ ...prev, description: e.target.value }))
                }
                style={{ marginBottom: "5px" }}
                disabled={loading}
              />
            </Col>
            <Col span={8}>
              <Input
                placeholder="Lambda ARN"
                value={actionGroupInput.lambdaArn}
                onChange={(e) =>
                  setActionGroupInput((prev) => ({ ...prev, lambdaArn: e.target.value }))
                }
                style={{ marginBottom: "5px" }}
                disabled={loading}
                status={errors.actionGroupInputLambda ? "error" : ""}
                suffix={errors.actionGroupInputLambda ? <ClockCircleOutlined /> : null}
              />
              {errors.actionGroupInputLambda && (
                <span style={{ color: "red", fontSize: "12px" }}>{errors.actionGroupInputLambda}</span>
              )}
            </Col>
          </Row>
          <Card type="inner" title="Agregar Parámetros" style={{ marginBottom: "10px" }}>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <Input
                  placeholder="Nombre"
                  value={newParameter.paramName}
                  onChange={(e) =>
                    setNewParameter((prev) => ({ ...prev, paramName: e.target.value }))
                  }
                  disabled={loading || maxParamsReached}
                  status={errors.newParameter ? "error" : ""}
                  suffix={errors.newParameter ? <ClockCircleOutlined /> : null}
                />
              </Col>
              <Col span={4}>
                <Select
                  value={newParameter.type}
                  onChange={(value) =>
                    setNewParameter((prev) => ({ ...prev, type: value }))
                  }
                  disabled={loading || maxParamsReached}
                >
                  <Option value="string">String</Option>
                  <Option value="number">Number</Option>
                  <Option value="boolean">Boolean</Option>
                </Select>
              </Col>
              <Col span={8}>
                <Input
                  placeholder="Descripción"
                  value={newParameter.description}
                  onChange={(e) =>
                    setNewParameter((prev) => ({ ...prev, description: e.target.value }))
                  }
                  disabled={loading || maxParamsReached}
                />
              </Col>
              <Col span={4}>
                <Checkbox
                  checked={newParameter.required}
                  onChange={(e) =>
                    setNewParameter((prev) => ({ ...prev, required: e.target.checked }))
                  }
                  disabled={loading || maxParamsReached}
                >
                  Requerido
                </Checkbox>
              </Col>
              <Col span={2}>
                <Button type="primary" onClick={handleAddParameter} disabled={loading || maxParamsReached}>
                  Agregar
                </Button>
              </Col>
            </Row>
            {maxParamsReached && (
              <Text type="warning" style={{ fontSize: "12px" }}>
                Se ha alcanzado el máximo de 5 parámetros.
              </Text>
            )}
            {errors.newParameter && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.newParameter}</span>
            )}
            <List
              bordered
              dataSource={actionGroupInput.parameters}
              renderItem={(param: Parameter, index: number) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      onClick={() => handleDeleteParameter(index)}
                      style={{ color: "red" }}
                      disabled={loading}
                    >
                      Eliminar
                    </Button>,
                  ]}
                >
                  <div>
                    <strong>{param.paramName}</strong> - {param.type} - {param.description}{" "}
                    {param.required && <span>(Requerido)</span>}
                  </div>
                </List.Item>
              )}
              style={{ marginTop: "10px" }}
            />
          </Card>
          <Button type="primary" onClick={handleAddActionGroup} disabled={loading}>
            Agregar Action Group
          </Button>
          {errors.actionGroups && (
            <span style={{ color: "red", fontSize: "12px", marginTop: "5px", display: "block" }}>
              {errors.actionGroups}
            </span>
          )}
          <List
            bordered
            dataSource={actionGroupsList}
            renderItem={(group, index) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    onClick={() => handleDeleteActionGroup(index)}
                    style={{ color: "red" }}
                    disabled={loading}
                  >
                    Eliminar
                  </Button>,
                ]}
              >
                <div>
                  <strong>{group.name}</strong>
                  <div>{group.description}</div>
                  <div>
                    <em>Lambda ARN:</em> {group.lambdaArn}
                  </div>
                  <div>
                    <em>Parámetros:</em>{" "}
                    {group.parameters && group.parameters.length > 0 ? (
                      <ul style={{ paddingLeft: "20px", margin: 0 }}>
                        {group.parameters.map((param: Parameter, i: number) => (
                          <li key={i}>
                            <strong>{param.paramName}</strong> - {param.type} - {param.description}{" "}
                            {param.required && <span>(Requerido)</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      "Sin parámetros"
                    )}
                  </div>
                </div>
              </List.Item>
            )}
            style={{ marginTop: "10px" }}
          />
        </Card>
      )}
    </Modal>
  );
};

export default AgentsModal;